import { downloadBlobFile } from '../../core/utils/browser-file.util';

export interface RenterReportHeader {
  renterName: string;
  renterMobile: string;
  renterEmail: string;
  flat: string;
  houseName: string;
  floorName: string;
  flatName: string;
  status: string;
  startDate: string;
  endDate: string;
  advanceAmount: string;
  advanceUsed: string;
  advanceRemain: string;
  year: string;
  negativeChargeTitle: string;
}

export interface RenterReportRow {
  monthName: string;
  paymentDate: string;
  homeRent: string;
  gasBill: string;
  electricBill: string;
  waterBill: string;
  serviceCharge: string;
  otherCharges: string;
  advanceAdjusted: string;
  totalAmount: string;
}

export interface RenterReportData {
  header: RenterReportHeader;
  rows: RenterReportRow[];
  brandName?: string;
  brandLogoUrl?: string | null;
  generatedOn: string;
  generatedBy: string;
  developedBy: string;
}

type PdfImageFormat = 'PNG' | 'JPEG' | 'WEBP';

async function loadImageForPdf(path: string): Promise<{ dataUrl: string; format: PdfImageFormat } | null> {
  try {
    if (!path) {
      return null;
    }

    if (path.startsWith('data:image/')) {
      return {
        dataUrl: path,
        format: detectPdfImageFormat(path.slice(5, path.indexOf(';')))
      };
    }

    const response = await fetch(path);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const format = detectPdfImageFormat(blob.type);
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read logo image.'));
      reader.readAsDataURL(blob);
    });
    return { dataUrl, format };
  } catch {
    return null;
  }
}

export async function downloadRenterReportPdf(data: RenterReportData): Promise<void> {
  const brandName = `${data.brandName ?? ''}`.trim() || 'HTALLY';
  const brandLogoUrl = `${data.brandLogoUrl ?? ''}`.trim();
  const fallbackLogoPath = !brandLogoUrl && brandName === 'HTALLY' ? '/HTallyIcon.png' : '';

  const [{ jsPDF }, logoImage] = await Promise.all([
    import('jspdf'),
    loadImageForPdf(brandLogoUrl || fallbackLogoPath)
  ]);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const reportFileName = buildRenterReportFileName(data, 'pdf');
  doc.setProperties({
    title: reportFileName.replace(/\.pdf$/i, ''),
    subject: 'Renter Payment Report'
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const left = 34;
  const right = 34;
  const usableW = pageW - left - right;
  let y = 34;

  const color = {
    titleBg: [224, 246, 250] as [number, number, number],
    titleText: [26, 152, 182] as [number, number, number],
    cardBg: [245, 248, 255] as [number, number, number],
    cardBorder: [220, 229, 245] as [number, number, number],
    label: [109, 123, 161] as [number, number, number],
    value: [31, 38, 65] as [number, number, number],
    tableHeadBg: [234, 242, 255] as [number, number, number],
    tableHeadText: [31, 38, 65] as [number, number, number],
    rowAltBg: [250, 252, 255] as [number, number, number],
    line: [220, 229, 245] as [number, number, number],
    totalBg: [237, 245, 255] as [number, number, number]
  };

  const tableCols = [
    'Month',
    'Payment Date',
    'Rent',
    'Gas',
    'Electric',
    'Water',
    'Service + Other',
    normalizeHeaderLabel(data.header.negativeChargeTitle || 'Negative Charge', 16),
    'Adjust From Advance',
    'Total'
  ];
  const colW = [
    usableW * 0.14,
    usableW * 0.11,
    usableW * 0.085,
    usableW * 0.085,
    usableW * 0.085,
    usableW * 0.085,
    usableW * 0.095,
    usableW * 0.09,
    usableW * 0.095,
    usableW * 0.13
  ];
  const rowH = 20;
  const headerRowH = 28;

  const titleCardHeight = 56;
  doc.setFillColor(...color.titleBg);
  doc.roundedRect(left, y, usableW, titleCardHeight, 10, 10, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...color.titleText);
  doc.text('Renter Payment Report', left + 14, y + 24);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...color.label);
  doc.text(fitTextToWidth(doc, brandName, usableW - 80), left + 14, y + 41);
  if (logoImage) {
    const logoWidth = 30;
    const logoHeight = 30;
    const logoX = left + usableW - logoWidth - 14;
    doc.addImage(logoImage.dataUrl, logoImage.format, logoX, y + 13, logoWidth, logoHeight);
  }
  y += titleCardHeight + 12;

  const gap = 10;
  const cardW = (usableW - gap * 2) / 3;
  const cardH = 118;

  const drawCard = (x: number, top: number, title: string, lines: string[]) => {
    doc.setFillColor(...color.cardBg);
    doc.setDrawColor(...color.cardBorder);
    doc.roundedRect(x, top, cardW, cardH, 8, 8, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...color.value);
    doc.text(title, x + 10, top + 16);

    let lineY = top + 34;
    const contentW = cardW - 20;
    lines.forEach((line) => {
      const idx = line.indexOf(':');
      if (idx > -1) {
        const key = `${line.slice(0, idx + 1)} `;
        const val = line.slice(idx + 1).trim();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...color.label);
        doc.text(key, x + 10, lineY);
        const keyW = doc.getTextWidth(key);
        const availableW = Math.max(18, contentW - keyW);
        const wrapped = doc.splitTextToSize(val || '-', availableW);
        const limited = wrapped.slice(0, 2);
        if (wrapped.length > 2) {
          limited[1] = `${limited[1]}...`;
        }
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...color.value);
        limited.forEach((part: string, i: number) => {
          doc.text(part, x + 10 + keyW, lineY + i * 12);
        });
        lineY += Math.max(14, limited.length * 12);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...color.value);
        const wrapped = doc.splitTextToSize(line || '-', contentW).slice(0, 2);
        wrapped.forEach((part: string, i: number) => {
          doc.text(part, x + 10, lineY + i * 12);
        });
        lineY += Math.max(14, wrapped.length * 12);
        return;
      }
    });

  };

  drawCard(left, y, 'Renter Information', [
    `Name: ${data.header.renterName}`,
    `Mobile: ${data.header.renterMobile}`,
    `Email: ${data.header.renterEmail}`
  ]);
  drawCard(left + cardW + gap, y, 'Home Information', [
    `Flat: ${data.header.flat}`,
    `Status: ${data.header.status}`,
    `Start Date: ${data.header.startDate}`,
    `End Date: ${data.header.endDate || 'N/A'}`
  ]);
  drawCard(left + (cardW + gap) * 2, y, 'Advance & Filter', [
    `Advance: ${data.header.advanceAmount}`,
    `Used: ${data.header.advanceUsed}`,
    `Remain: ${data.header.advanceRemain}`,
    `Year: ${data.header.year}`
  ]);
  y += cardH + 14;

  const drawTableHeader = (headerY: number) => {
    doc.setFillColor(...color.tableHeadBg);
    doc.setDrawColor(...color.line);
    doc.rect(left, headerY, usableW, headerRowH, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...color.tableHeadText);
    let x = left;
    tableCols.forEach((col, i) => {
      const lines = wrapHeaderLines(doc, col, Math.max(22, colW[i] - 10), 2);
      const tx = x + (colW[i] / 2);
      const baseY = headerY + 10;
      lines.forEach((line, idx) => {
        doc.text(line, tx, baseY + (idx * 8), { align: 'center' });
      });
      x += colW[i];
    });
  };

  drawTableHeader(y);
  y += headerRowH;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.3);
  doc.setTextColor(...color.value);
  const rows = data.rows.length > 0 ? data.rows : [{
    monthName: 'No data',
    paymentDate: '-',
    homeRent: '-',
    gasBill: '-',
    electricBill: '-',
    waterBill: '-',
    serviceCharge: '-',
    otherCharges: '-',
    advanceAdjusted: '-',
    totalAmount: '-'
  }];

  rows.forEach((row, rowIdx) => {
    if (y + rowH > pageH - 36) {
      doc.addPage();
      y = 34;
      drawTableHeader(y);
      y += headerRowH;
    }

    if (rowIdx % 2 === 1) {
      doc.setFillColor(...color.rowAltBg);
      doc.rect(left, y, usableW, rowH, 'F');
    }

    const values = [
      row.monthName,
      row.paymentDate,
      row.homeRent,
      row.gasBill,
      row.electricBill,
      row.waterBill,
      row.serviceCharge,
      row.otherCharges,
      row.advanceAdjusted,
      row.totalAmount
    ];
    let x = left;
    values.forEach((value, i) => {
      const align: 'left' | 'right' = i < 2 ? 'left' : 'right';
      const tx = align === 'left' ? x + 5 : x + colW[i] - 5;
      doc.text(`${value ?? ''}`, tx, y + 13, { align });
      x += colW[i];
    });
    doc.setDrawColor(...color.line);
    doc.line(left, y + rowH, left + usableW, y + rowH);
    y += rowH;
  });

  const totals = calculateTotals(data.rows);
  if (y + rowH > pageH - 36) {
    doc.addPage();
    y = 34;
    drawTableHeader(y);
    y += headerRowH;
  }
  doc.setFillColor(...color.totalBg);
  doc.rect(left, y, usableW, rowH, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...color.value);
  const totalValues = [
    'Total',
    '-',
    formatNumber(totals.rent),
    formatNumber(totals.gas),
    formatNumber(totals.electric),
    formatNumber(totals.water),
    formatNumber(totals.service),
    formatDeductionNumber(totals.other),
    formatDeductionNumber(totals.advanceAdjusted),
    formatNumber(totals.total)
  ];
  let totalX = left;
  totalValues.forEach((value, i) => {
    const align: 'left' | 'right' = i < 2 ? 'left' : 'right';
    const tx = align === 'left' ? totalX + 5 : totalX + colW[i] - 5;
    doc.text(`${value}`, tx, y + 13, { align });
    totalX += colW[i];
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    const footerY = pageH - 18;
    doc.setDrawColor(...color.line);
    doc.line(left, footerY - 12, pageW - right, footerY - 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...color.label);
    doc.text(`Generated On: ${data.generatedOn}`, left, footerY);
    doc.text(`Generated By: ${data.generatedBy}`, left + (usableW * 0.43), footerY);
    doc.text(`Developed by: ${data.developedBy}`, pageW - right, footerY, { align: 'right' });
  }

  downloadBlobFile(doc.output('blob') as Blob, reportFileName);
}

export function downloadRenterReportExcel(data: RenterReportData): void {
  const rows = data.rows.length > 0 ? data.rows : [{
    monthName: 'No data',
    paymentDate: '-',
    homeRent: '-',
    gasBill: '-',
    electricBill: '-',
    waterBill: '-',
    serviceCharge: '-',
    otherCharges: '-',
    advanceAdjusted: '-',
    totalAmount: '-'
  }];

  const totals = calculateTotals(data.rows);
  const metaRows = [
    ['Name', data.header.renterName],
    ['Mobile', data.header.renterMobile],
    ['Email', data.header.renterEmail],
    ['Flat', data.header.flat],
    ['Status', data.header.status],
    ['Start Date', data.header.startDate],
    ['End Date', data.header.endDate || 'N/A'],
    ['Advance', data.header.advanceAmount],
    ['Used', data.header.advanceUsed],
    ['Remain', data.header.advanceRemain],
    ['Year', data.header.year]
  ];

  const paymentHeader = ['Month', 'Year', 'Payment Date', 'Rent', 'Gas', 'Electric', 'Water', 'Service + Other', data.header.negativeChargeTitle || 'Negative Charge', 'Adjust From Advance', 'Total'];
  const paymentRowsXml = rows.map((row) => {
    const monthText = `${row.monthName ?? ''}`;
    const [monthName, yearRaw] = monthText.split(',').map((x) => `${x ?? ''}`.trim());
    const yearText = yearRaw || '';
    return `<Row>
      <Cell ss:StyleID="sData"><Data ss:Type="String">${escapeXml(monthName || monthText)}</Data></Cell>
      <Cell ss:StyleID="sYear"><Data ss:Type="String">${escapeXml(yearText)}</Data></Cell>
      <Cell ss:StyleID="sData"><Data ss:Type="String">${escapeXml(row.paymentDate)}</Data></Cell>
      <Cell ss:StyleID="sNumber"><Data ss:Type="Number">${parseNumeric(row.homeRent)}</Data></Cell>
      <Cell ss:StyleID="sNumber"><Data ss:Type="Number">${parseNumeric(row.gasBill)}</Data></Cell>
      <Cell ss:StyleID="sNumber"><Data ss:Type="Number">${parseNumeric(row.electricBill)}</Data></Cell>
      <Cell ss:StyleID="sNumber"><Data ss:Type="Number">${parseNumeric(row.waterBill)}</Data></Cell>
      <Cell ss:StyleID="sNumber"><Data ss:Type="Number">${parseNumeric(row.serviceCharge)}</Data></Cell>
      <Cell ss:StyleID="sNumber"><Data ss:Type="Number">${parseNumeric(row.otherCharges)}</Data></Cell>
      <Cell ss:StyleID="sNumber"><Data ss:Type="Number">${parseNumeric(row.advanceAdjusted)}</Data></Cell>
      <Cell ss:StyleID="sNumberBold"><Data ss:Type="Number">${parseNumeric(row.totalAmount)}</Data></Cell>
    </Row>`;
  }).join('');

  const xml = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
  <Styles>
    <Style ss:ID="Default" ss:Name="Normal">
      <Alignment ss:Vertical="Center"/>
      <Font ss:FontName="Calibri" ss:Size="11"/>
    </Style>
    <Style ss:ID="sTitle">
      <Font ss:FontName="Calibri" ss:Size="14" ss:Bold="1" ss:Color="#1A98B6"/>
    </Style>
    <Style ss:ID="sMetaHead">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1F2641"/>
      <Interior ss:Color="#EEF5FF" ss:Pattern="Solid"/>
    </Style>
    <Style ss:ID="sMetaKey">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1F2641"/>
      <Interior ss:Color="#F7FAFF" ss:Pattern="Solid"/>
    </Style>
    <Style ss:ID="sMetaVal">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#1F2641"/>
    </Style>
    <Style ss:ID="sTableHead">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1F2641"/>
      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
      <Interior ss:Color="#E0F6FA" ss:Pattern="Solid"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CDE1F5"/>
      </Borders>
    </Style>
    <Style ss:ID="sData">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#1F2641"/>
      <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
    </Style>
    <Style ss:ID="sYear">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1A98B6"/>
      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
    </Style>
    <Style ss:ID="sNumber">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#1F2641"/>
      <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
      <NumberFormat ss:Format="#,##0"/>
    </Style>
    <Style ss:ID="sNumberBold">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1F2641"/>
      <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
      <NumberFormat ss:Format="#,##0"/>
    </Style>
    <Style ss:ID="sTotalLabel">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1F2641"/>
      <Interior ss:Color="#EEF5FF" ss:Pattern="Solid"/>
    </Style>
    <Style ss:ID="sTotalNum">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#1F2641"/>
      <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
      <Interior ss:Color="#EEF5FF" ss:Pattern="Solid"/>
      <NumberFormat ss:Format="#,##0"/>
    </Style>
  </Styles>
  <Worksheet ss:Name="Renter Report">
    <Table>
      <Column ss:Width="150"/>
      <Column ss:Width="80"/>
      <Column ss:Width="110"/>
      <Column ss:Width="85"/>
      <Column ss:Width="75"/>
      <Column ss:Width="75"/>
      <Column ss:Width="75"/>
      <Column ss:Width="80"/>
      <Column ss:Width="80"/>
      <Column ss:Width="90"/>
      <Column ss:Width="90"/>
      <Row><Cell ss:StyleID="sTitle" ss:MergeAcross="10"><Data ss:Type="String">Renter Payment Report</Data></Cell></Row>
      <Row/>
      <Row>
        <Cell ss:StyleID="sMetaHead"><Data ss:Type="String">Field</Data></Cell>
        <Cell ss:StyleID="sMetaHead" ss:MergeAcross="3"><Data ss:Type="String">Value</Data></Cell>
      </Row>
      ${metaRows.map(([k, v]) => `<Row>
        <Cell ss:StyleID="sMetaKey"><Data ss:Type="String">${escapeXml(k)}</Data></Cell>
        <Cell ss:StyleID="sMetaVal" ss:MergeAcross="3"><Data ss:Type="String">${escapeXml(v)}</Data></Cell>
      </Row>`).join('')}
      <Row/>
      <Row>${paymentHeader.map((h) => `<Cell ss:StyleID="sTableHead"><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join('')}</Row>
      ${paymentRowsXml}
      <Row>
        <Cell ss:StyleID="sTotalLabel"><Data ss:Type="String">Final Total</Data></Cell>
        <Cell ss:StyleID="sTotalLabel"><Data ss:Type="String"></Data></Cell>
        <Cell ss:StyleID="sTotalLabel"><Data ss:Type="String">-</Data></Cell>
        <Cell ss:StyleID="sTotalNum"><Data ss:Type="Number">${totals.rent}</Data></Cell>
        <Cell ss:StyleID="sTotalNum"><Data ss:Type="Number">${totals.gas}</Data></Cell>
        <Cell ss:StyleID="sTotalNum"><Data ss:Type="Number">${totals.electric}</Data></Cell>
        <Cell ss:StyleID="sTotalNum"><Data ss:Type="Number">${totals.water}</Data></Cell>
        <Cell ss:StyleID="sTotalNum"><Data ss:Type="Number">${totals.service}</Data></Cell>
        <Cell ss:StyleID="sTotalNum"><Data ss:Type="Number">${totals.other}</Data></Cell>
        <Cell ss:StyleID="sTotalNum"><Data ss:Type="Number">${totals.advanceAdjusted}</Data></Cell>
        <Cell ss:StyleID="sTotalNum"><Data ss:Type="Number">${totals.total}</Data></Cell>
      </Row>
    </Table>
  </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = buildRenterReportFileName(data, 'xls');
  anchor.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(value: string | number | null | undefined): string {
  const text = `${value ?? ''}`;
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeFilePart(value: string | null | undefined): string {
  const raw = `${value ?? ''}`.trim().toLowerCase();
  const cleaned = raw.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'renter';
}

function buildRenterReportFileName(data: RenterReportData, extension: 'pdf' | 'xls'): string {
  const renterName = safeFilePart(data.header.renterName);
  const year = safeFilePart(data.header.year);
  const houseName = safeFilePart(data.header.houseName);
  const floorName = safeFilePart(data.header.floorName);
  const flatName = safeFilePart(data.header.flatName);
  return `renter-details-report-${renterName}-${year}-${houseName}-${floorName}-${flatName}.${extension}`;
}

function csvCell(value: string | number | null | undefined): string {
  const text = `${value ?? ''}`.replace(/"/g, '""');
  return `"${text}"`;
}

function escapeXml(value: string | number | null | undefined): string {
  const text = `${value ?? ''}`;
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function formatNumber(value: number): string {
  return Number(value || 0).toLocaleString('en-US');
}

function formatDeductionNumber(value: number): string {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) {
    return '0';
  }
  return `(${amount.toLocaleString('en-US')})`;
}

function detectPdfImageFormat(mimeType: string): PdfImageFormat {
  const normalized = `${mimeType ?? ''}`.trim().toLowerCase();
  if (normalized.includes('png')) {
    return 'PNG';
  }
  if (normalized.includes('webp')) {
    return 'WEBP';
  }
  return 'JPEG';
}

function parseNumeric(value: string | number | null | undefined): number {
  const raw = `${value ?? ''}`.trim();
  const text = raw
    .replace(/[()]/g, '')
    .replace(/,/g, '')
    .trim();
  const parsed = Number(text);
  return Number.isFinite(parsed) ? Math.abs(parsed) : 0;
}

function normalizeHeaderLabel(value: string | null | undefined, maxChars: number): string {
  const text = `${value ?? ''}`.trim() || 'Negative Charge';
  if (text.length <= maxChars) {
    return text;
  }
  return `${text.slice(0, Math.max(1, maxChars - 1))}…`;
}

function fitTextToWidth(doc: any, value: string, maxWidth: number): string {
  const text = `${value ?? ''}`.trim();
  if (!text || doc.getTextWidth(text) <= maxWidth) {
    return text;
  }
  const ellipsis = '…';
  let output = text;
  while (output.length > 1 && doc.getTextWidth(`${output}${ellipsis}`) > maxWidth) {
    output = output.slice(0, -1);
  }
  return `${output}${ellipsis}`;
}

function wrapHeaderLines(doc: any, value: string, maxWidth: number, maxLines = 2): string[] {
  const text = `${value ?? ''}`.trim();
  if (!text) {
    return [''];
  }
  const wrapped = (doc.splitTextToSize(text, maxWidth) as string[]) || [text];
  if (wrapped.length <= maxLines) {
    return wrapped;
  }
  const lines = wrapped.slice(0, maxLines);
  const lastIndex = maxLines - 1;
  lines[lastIndex] = fitTextToWidth(doc, lines[lastIndex], maxWidth);
  return lines;
}

function calculateTotals(rows: RenterReportRow[]): {
  rent: number;
  gas: number;
  electric: number;
  water: number;
  service: number;
  other: number;
  total: number;
  advanceAdjusted: number;
} {
  return rows.reduce((acc, row) => {
    acc.rent += parseNumeric(row.homeRent);
    acc.gas += parseNumeric(row.gasBill);
    acc.electric += parseNumeric(row.electricBill);
    acc.water += parseNumeric(row.waterBill);
    acc.service += parseNumeric(row.serviceCharge);
    acc.other += parseNumeric(row.otherCharges);
    acc.advanceAdjusted += parseNumeric(row.advanceAdjusted);
    acc.total += parseNumeric(row.totalAmount);
    return acc;
  }, {
    rent: 0,
    gas: 0,
    electric: 0,
    water: 0,
    service: 0,
    other: 0,
    advanceAdjusted: 0,
    total: 0
  });
}
